<template>
  <div>
    <filter-bar
      :filters="filterSchema"
      :initial-value="filterValues"
      :loading="loading"
      :result-count="rows.length"
      saved-filters-key="rating-scales"
      @change="onFiltersChanged"
    />

    <data-table
      class="mt-3"
      :columns="columns"
      :rows="rows"
      row-key="id"
      :loading="loading"
      :reorderable="!showArchived && !filterValues.name"
      @reorder="onReorder"
    >
      <template #cell(name)="{ row }">
        <span class="data-table__primary">{{ row.name }}</span>
      </template>
      <template #cell(preview)="{ row }">
        <rating-display v-if="row.display_type === 'icons'" :value="ratingPreviewValue(row)" :scale="row" size="sm" />
        <span v-else class="tags-row">
          <span v-for="(level, i) in row.levels" :key="i" class="tag-chip text-white" :style="tagChipStyle(row.color)">{{ level }}</span>
        </span>
      </template>
      <template #cell(rated_event_count)="{ row }">
        <span>{{ row.rated_event_count || 0 }}</span>
      </template>
      <template #actions="{ row }">
        <el-dropdown>
          <base-button simple type="primary" link size="sm">
            <i class="tim-icons el-icon-more"></i>
          </base-button>
          <template #dropdown>
            <el-dropdown-menu>
              <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="openEdit(row)">
                <i class="tim-icons text-default icon-pencil mr-2"></i> Edit scale
              </a>
              <hr>
              <a v-if="row.archived" class="dropdown-item" href="#" @click.prevent="setArchived(row, 0)">
                <span class="active-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Restore scale
              </a>
              <a v-else class="dropdown-item" href="#" @click.prevent="confirmArchive(row)">
                <span class="inactive-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Archive scale
              </a>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
      <template #empty>
        <img src="/img/pandas/empty-events.png" alt="" class="empty-state-img empty-state-img--md" />
        <h4 class="mt-3 empty-state-text">{{ emptyState.title }}</h4>
        <p class="data-table__muted">{{ emptyState.text }}</p>
      </template>
    </data-table>

    <rating-scale-drawer :show.sync="drawer.show" :scale="drawer.scale" @saved="onSaved" />

    <base-alert-dialog
      :show="dialog.show"
      :variant="dialog.variant"
      :title="dialog.title"
      :message="dialog.message"
      :show-cancel="dialog.showCancel"
      :confirm-text="dialog.confirmText"
      :confirm-variant="dialog.confirmVariant"
      @confirm="onDialogConfirm"
      @cancel="dialog.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { Dropdown, DropdownMenu } from 'element-ui'
import { API_LOCATION } from '@/api-config'
import RatingScaleDrawer from './RatingScaleDrawer.vue'
import { ratingPreviewValue } from '@/util/ratings'
import { tagChipStyle } from '@/util/tagChip'
import type { RatingScale } from '@/util/ratings'

// Ratings tab of Event settings: the venue's rating scales. Active scales drag to reorder; the
// first active scale is the one emails and the Community API show when an event has several.
const ElDropdown = Dropdown
const ElDropdownMenu = DropdownMenu

const emit = defineEmits(['changed'])
const { proxy } = getCurrentInstance() as any

const columns = [
  { key: 'name', label: 'Name' },
  { key: 'preview', label: 'Scale' },
  { key: 'rated_event_count', label: 'Events rated', align: 'right' as const },
]
// Same filters as the Events tab: a name search and the Archived switch.
const filterSchema = [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'Any part of the name', operatorLabel: 'contains', primary: true },
  { key: 'archived', type: 'toggle', label: 'Archived', primary: true },
]

const scales = ref<RatingScale[]>([])
const loading = ref(false)
const filterValues = ref<{ name: string, archived: boolean }>({ name: '', archived: false })
const drawer = ref<{ show: boolean, scale: RatingScale | null }>({ show: false, scale: null })
const dialog = ref({
  show: false, variant: 'warning', title: '', message: '', showCancel: false,
  confirmText: 'OK', confirmVariant: 'primary', onConfirm: null as null | (() => void),
})

const showArchived = computed(() => !!filterValues.value.archived)
const rows = computed(() => {
  const needle = (filterValues.value.name || '').trim().toLowerCase()
  return scales.value.filter(s => s.archived === showArchived.value && (!needle || s.name.toLowerCase().includes(needle)))
})
const emptyState = computed(() => {
  if (filterValues.value.name) return { title: 'No rating scales match these filters', text: 'Try a different name.' }
  if (showArchived.value) return { title: 'No archived scales', text: 'Scales you archive show up here.' }
  return { title: 'No rating scales yet', text: 'Add a scale to rate your events, for example difficulty or scare level.' }
})

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { name: values.name || '', archived: values.archived === true }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function baseUrl() {
  const companyId = localStorage.getItem('companyId')
  const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
  return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/rating-scales`
}

function showError(message: string) {
  dialog.value = { show: true, variant: 'danger', title: 'Something went wrong', message, showCancel: false, confirmText: 'OK', confirmVariant: 'primary', onConfirm: null }
}

async function load() {
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(baseUrl(), config)
    scales.value = response.data.scales || []
  } catch (err) {
    console.log(err)
    showError('The rating scales could not be loaded. Please refresh and try again.')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  drawer.value = { show: true, scale: null }
}

function openEdit(row: RatingScale) {
  drawer.value = { show: true, scale: row }
}

async function onSaved() {
  await load()
  emit('changed')
}

// Optimistic reorder: the new order shows on drop and saves in the background; a failed save reloads.
async function onReorder(newRows: RatingScale[]) {
  const archived = scales.value.filter(s => s.archived)
  scales.value = [...newRows, ...archived]
  try {
    const config = await authConfig()
    await axios.put(baseUrl(), { scales: newRows.map(r => ({ id: r.id })) }, config)
    proxy.$notify({ type: 'success', message: 'Scale order saved', icon: 'tim-icons icon-check-2', timeout: 2000 })
    emit('changed')
  } catch (err) {
    console.log(err)
    proxy.$notify({ type: 'danger', message: 'Could not save the new scale order', icon: 'tim-icons icon-alert-circle-exc' })
    load()
  }
}

function confirmArchive(row: RatingScale) {
  dialog.value = {
    show: true,
    variant: 'warning',
    title: 'Archive this scale?',
    message: 'Its ratings stop showing on your booking site, in emails and in the API. You can restore it later.',
    showCancel: true,
    confirmText: 'Archive',
    confirmVariant: 'danger',
    onConfirm: () => setArchived(row, 1),
  }
}

function onDialogConfirm() {
  const action = dialog.value.onConfirm
  dialog.value.show = false
  if (action) action()
}

// The update endpoint takes the full scale; only `archived` changes here.
async function setArchived(row: RatingScale, archived: number) {
  try {
    const config = await authConfig()
    const payload: Record<string, any> = {
      name: row.name,
      display_type: row.display_type,
      max_value: row.max_value,
      allow_half: row.allow_half ? 1 : 0,
      icon: row.icon,
      color: row.color,
      archived,
    }
    if (row.display_type === 'labels') {
      payload.levels = row.levels.map((name, i) => ({ name, previous_position: i + 1 }))
    }
    await axios.put(`${baseUrl()}/${row.id}`, payload, config)
    proxy.$notify({ type: 'success', message: archived ? 'Scale archived' : 'Scale restored', icon: 'tim-icons icon-check-2' })
    await load()
    emit('changed')
  } catch (err: any) {
    console.log(err)
    let body = err?.response?.data
    if (typeof body === 'string') {
      try { body = JSON.parse(body) } catch (e) { body = { error: body } }
    }
    const limit = body?.validation_errors?.scales
    showError(limit || (archived ? 'The scale could not be archived.' : 'The scale could not be restored.'))
  }
}

defineExpose({ openCreate })

onMounted(load)
</script>
